<script setup lang="ts">
    import { storeToRefs } from 'pinia';

    const { modules } = useModules();
    const { authed, tenant, tenants, isImpersonating } = storeToRefs(useAuthStore());
    const logout = useLogout();
    const themeStore = useThemeStore();
    const { theme } = storeToRefs(themeStore);
    const userSubmenu = ref(null);

    const saveLocale = (newLocale: string) => {
        useSaveLocale(newLocale);
        userSubmenu.value?.hide();
    };

    onMounted(useGetTenants);

    const { $tracker } = useNuxtApp();

    defineProps<{
        isSidebar?: boolean;
        collapsed?: boolean;
        isBurger?: boolean;
    }>();

    const openAccount = (toggle: () => void) => {
        $tracker.track('sidebar_account_clicked');
        toggle();
    };

    // Unpaid level 4 (TDD-5603): the "Mon compte" menu stays fully usable
    // (language, QR login, switch account, logout), only search is disabled.
    const { isBlocked: isUnpaidBlocked } = useUnpaidStatus();
    const openSearch = () => {
        if (isUnpaidBlocked.value) {
            useUnpaidTracking().track('unpaid_locked_item_clicked', { module: 'search' });
            return;
        }
        useEvent('app:search:open');
        userSubmenu.value?.hide();
    };
</script>

<template>
    <Submenu
        v-if="authed"
        ref="userSubmenu"
        :position="isSidebar ? 'left-0' : 'right-0'"
        :alignment="isSidebar ? 'bottom-full' : 'top-0'"
        without-max-height
    >
        <template #default="slotProps">
            <div
                class="relative flex items-center cursor-pointer dark:bg-black dark:hover:bg-blue-900 py-2"
                :class="[
                    isSidebar ? 'justify-start md:hover:bg-branding-rich-black rounded-md px-3' : 'bg-neutral-100 hover:bg-neutral-200 rounded-full pl-2 pr-4',
                    isBurger ? 'text-black' : 'text-white',
                ]"
                :style="isSidebar ? 'min-width: fit-content;' : ''"
                @click.prevent="openAccount(slotProps.toggle)"
            >
                <!-- Avatar avec position absolue pour être complètement stable -->
                <div
                    class="bg-blue-400 bg-center bg-cover rounded-full h-5 w-5"
                    :class="[isSidebar ? 'h-5 w-5' : 'h-8 w-8', isSidebar ? 'relative' : 'relative']"
                    :style="`background-image: url(${authed?.avatar})`"
                ></div>

                <!-- Conteneur pour les éléments qui peuvent disparaître avec width fixe -->
                <transition
                    enter-active-class="transition-all duration-1000 ease-in-out"
                    enter-from-class="w-0 opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="transition-all duration-500 ease-in-out"
                    leave-from-class="opacity-100"
                    leave-to-class="w-0 opacity-0"
                >
                    <div
                        v-if="isSidebar && !collapsed"
                        class="overflow-hidden ml-[14px]"
                    >
                        <div class="whitespace-nowrap">
                            <div>
                                <span class="text-sm font-inter max-md:font-medium">{{ $t('auth.myAccount') }}</span>
                            </div>
                            <!-- Probablement plus necessaire -->
                            <!-- <div v-else>
                                <div class="relative flex-col hidden space-y-1 md:flex">
                                    <span class="text-sm leading-none dark:text-gray-200 truncate">
                                        {{ authed?.fullName }}
                                    </span>
                                    <div class="flex items-center space-x-1">
                                        <div
                                            v-if="isImpersonating"
                                            class="h-[10px] w-[10px] bg-red-600 rounded-full flex-shrink-0"
                                        ></div>
                                        <span class="text-xs leading-none text-gray-600 dark:text-gray-400 truncate">
                                            {{ $t(`auth.user_role.${authed?.role}`) }}
                                        </span>
                                    </div>
                                </div>
                            </div> -->
                        </div>
                    </div>
                </transition>

                <!-- Chevron avec width fixe -->
                <div class="overflow-hidden ml-auto">
                    <transition
                        enter-active-class="transition-all duration-1000 ease-in-out"
                        enter-from-class="w-0 opacity-0"
                        enter-to-class="w-4 opacity-100"
                        leave-active-class="transition-all duration-500 ease-in-out"
                        leave-from-class="w-4 opacity-100"
                        leave-to-class="w-0 opacity-0"
                    >
                        <div
                            v-if="!collapsed"
                            class="hidden md:block w-4"
                        >
                            <i
                                class="text-sm text-gray-400 transition duration-100 rotate-0 fa-solid dark:text-gray-300 fa-arrow-up-right-from-square"
                                :class="[{ 'rotate-180': slotProps.isDisplayed }]"
                            />
                        </div>
                    </transition>
                </div>
            </div>
        </template>
        <!-- Modal -->
        <template #submenu>
            <div class="w-full min-w-[260px]">
                <transition
                    mode="out-in"
                    enter-active-class="transition-all duration-100 ease-out"
                    enter-from-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="transition-all duration-100 ease-out delay-150"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <!-- eslint-disable-next-line vue/require-toggle-inside-transition -- pre-existing: the Submenu slot mounts/unmounts this content itself -->
                    <div
                        :class="$theme('submenu.items_container')"
                        class="max-md:font-medium"
                    >
                        <div :class="$theme('submenu.items')">
                            <!-- TODO: Re-activate once the dark mode is done. -->
                            <!-- <a href="#" :class="$theme('submenu.item')" @click.prevent="themeStore.toggleTheme(); userSubmenu?.hide()">
                            <i class="fa-solid" :class="[theme == 'dark' ? 'fa-moon' : 'fa-sun-bright', $theme('submenu.item_icon')]" />
                            <span>{{ $t(`layout.header.user_submenu.switch_theme_${theme}`) }}</span>
                        </a> -->
                            <LanguageSwitcher @locale-changed="saveLocale">
                                <template #default="slotProps">
                                    <a
                                        href="#"
                                        :class="$theme('submenu.item')"
                                        @click.prevent="slotProps.toggle()"
                                    >
                                        <i
                                            class="fa-regular fa-globe"
                                            :class="$theme('submenu.item_icon')"
                                        />
                                        <span>{{ $t('layout.header.user_submenu.change_locale') }}</span>
                                    </a>
                                </template>
                            </LanguageSwitcher>
                            <a
                                href="#"
                                :class="$theme('submenu.item')"
                                @click.prevent="
                                    useEvent('auth:login-qrcode-modal:open');
                                    userSubmenu?.hide();
                                "
                            >
                                <i
                                    class="fa-regular fa-qrcode"
                                    :class="$theme('submenu.item_icon')"
                                />
                                <span>{{ $t('layout.header.user_submenu.login-qrcode') }}</span>
                            </a>
                            <a
                                href="#"
                                :class="[$theme('submenu.item'), { 'opacity-40 cursor-not-allowed': isUnpaidBlocked }]"
                                :aria-disabled="isUnpaidBlocked || undefined"
                                @click.prevent="openSearch"
                            >
                                <i
                                    class="fa-regular fa-search"
                                    :class="$theme('submenu.item_icon')"
                                />
                                <span>{{ $t('layout.header.user_submenu.search') }}</span>
                            </a>
                            <a
                                href="#"
                                :class="$theme('submenu.item')"
                                @click.prevent="logout()"
                            >
                                <i
                                    class="fa-regular fa-arrow-right-from-bracket"
                                    :class="$theme('submenu.item_icon')"
                                />
                                <span>{{ $t('layout.header.user_submenu.logout') }}</span>
                            </a>
                        </div>
                        <!-- No need -->
                        <!-- <div
                            v-if="!isSidebar"
                            :class="$theme('submenu.items')"
                        >
                            <div
                                v-for="availableModule in modules"
                                :key="availableModule.key"
                            >
                                <NuxtLink
                                    v-can="availableModule.checkPermission ? `${availableModule.key}:access` : null"
                                    :to="`/${availableModule.key}/${availableModule.defaultPath()}`"
                                    :class="[
                                        $theme('submenu.item'),
                                        {
                                            'bg-neutral-100': availableModule.current,
                                        },
                                    ]"
                                    @click.native="userSubmenu?.hide()"
                                >
                                    <i :class="$theme('submenu.item_icon', `fa-regular fa-${availableModule.icon}`)" />
                                    <span>{{ $t(`modules.${availableModule.key}`) }}</span>
                                </NuxtLink>
                            </div>
                        </div> -->

                        <div
                            v-if="tenants && tenants.count() > 1"
                            :class="$theme('submenu.items')"
                        >
                            <div :class="$theme('submenu.item_without_icon', 'cursor-default flex items-center justify-between')">
                                <div class="flex items-center space-x-2">
                                    <div
                                        class="w-6 h-6 bg-center bg-cover rounded-lg"
                                        :class="{ 'bg-neutral-200': !tenant!.logoUrl }"
                                        :style="`background-image: url(${tenant!.logoUrl})`"
                                    ></div>
                                    <span>{{ tenant!.name }}</span>
                                </div>
                                <span>
                                    <i
                                        class="text-sm fa-regular fa-circle-check"
                                        :class="$theme('submenu.item_icon')"
                                    />
                                </span>
                            </div>
                            <Submenu
                                container="absolute z-[100] p-4 shadow-xl bg-white dark:bg-black whitespace-nowrap border border-gray-100 dark:border-black rounded-lg outline-none"
                                :position="isBurger ? 'left-0' : 'left-full'"
                                :alignment="isSidebar ? 'bottom-0' : 'top-0'"
                            >
                                <template #default="slotProps">
                                    <a
                                        href="#"
                                        :class="$theme('submenu.item')"
                                        @click.prevent="slotProps.toggle()"
                                    >
                                        <div class="w-6 text-center">
                                            <i :class="$theme('submenu.item_icon', 'fa-regular fa-building-user')" />
                                        </div>
                                        <span>{{ $t('layout.header.user_submenu.change_account') }}</span>
                                    </a>
                                </template>
                                <template #submenu>
                                    <div class="w-full min-w-[200px] h-full">
                                        <template v-for="availableTenant in tenants">
                                            <NuxtLink
                                                v-if="availableTenant.getId() !== tenant.getId()"
                                                :key="availableTenant.getInternalId()"
                                                :to="`/switch-tenant?tenant=${availableTenant.getId()}`"
                                                :class="$theme('submenu.item_without_icon', 'flex items-center justify-between')"
                                            >
                                                <div class="flex items-center space-x-2">
                                                    <div
                                                        class="w-6 h-6 bg-center bg-cover rounded-lg"
                                                        :class="{ 'bg-neutral-200': !availableTenant.logoUrl }"
                                                        :style="`background-image: url(${availableTenant.logoUrl})`"
                                                    ></div>
                                                    <span>{{ availableTenant.name }}</span>
                                                </div>
                                            </NuxtLink>
                                        </template>
                                    </div>
                                </template>
                            </Submenu>
                        </div>
                    </div>
                </transition>
            </div>
        </template>
    </Submenu>
</template>
